/**
 * chatEngine.js — ตัวจับคู่คำถาม → คำตอบของผู้ช่วย HRVEC (ทำงานฝั่ง browser ทั้งหมด ไม่เรียก API)
 *
 * ขั้นตอน:
 *  1) normalize: ตัวพิมพ์เล็ก, ตัดเครื่องหมาย/ช่องว่าง, รวมคำพ้อง/คำสะกดผิดที่พบบ่อยให้เป็นรูปเดียว
 *     (ใช้ฟังก์ชันเดียวกันกับ keyword ใน knowledgeBase.js จึงเขียน keyword แบบไหนก็ได้)
 *  2) keyword scoring: keyword ที่พบในคำถามได้คะแนนตามความยาว (ยาว = เฉพาะเจาะจง) + โบนัสถ้าตรงกลุ่มผู้ใช้
 *  3) fuzzy fallback (fuse.js): กรณีพิมพ์ผิด/ไม่ตรง keyword เลย ใช้หาคำถามที่ใกล้เคียงมาเสนอให้เลือก
 */
import Fuse from "fuse.js";
import { INTENTS, QUICK_REPLIES } from "./knowledgeBase";

// คำพ้อง/คำสะกดผิด → รูปมาตรฐาน (ลำดับสำคัญ: วลียาวต้องมาก่อนคำสั้นที่เป็นส่วนหนึ่งของมัน)
const SYNONYMS = [
  [/thai\s*id|thai\s*d|ไทย\s*ไอดี|ไท\s*ไอดี|ไทยดี|ไทดี|ไทดีไอ/g, "thaid"],
  [/ล๊อค|ล็อค|ล๊อก|ล้อค|ล้อก/g, "ล็อก"],
  [/log\s*in|ล็อกอิน|เข้าระบบ|sign\s*in/g, "เข้าสู่ระบบ"],
  [/log\s*out|ล็อกเอาท์|ล็อกเอ้าท์|ออกระบบ/g, "ออกจากระบบ"],
  [/pass\s*word|พาสเวิร์ด|พาสเวิด|พาสเวิร์ท|รหัสผ่าน|รหัส\s*ผ่าน/g, "รหัสผ่าน"],
  [/รีเซ็ท|รีเซต|รีเซท|reset/g, "รีเซ็ต"],
  [/อัพเดท|อัพเดต|อัปเดท|update/g, "อัปเดต"],
  [/ปริ้นท์|ปริ้นต์|ปริ้น|ปรินท์|พริ้น|print/g, "พิมพ์"],
  [/ผ\.?\s*อ\./g, "ผอ"],
  [/ว\.?\s*ท\.?\s*ล\./g, "วิทยาลัย"],
  [/พนง\.?|พรก\.?/g, "พนักงานราชการ"],
  [/สอจ\./g, "สอจ"],
  [/อีเมล์|อีเมลล์|e-?mail|เมล์/g, "อีเมล"],
  [/โอทีพี/g, "otp"],
  [/เซสชั่น|เซสชัน|session/g, "session"],
  [/ครูผู้ชวย|ครูผช\.?/g, "ครูผู้ช่วย"],
  [/เงือนไข/g, "เงื่อนไข"],
  [/สิทธ์|สิทธ(?!ิ)/g, "สิทธิ์"]
];

const ROLE_BOOST = 0.75;
// ผู้ใช้ที่ login แล้วแต่ประเด็นนี้เป็นของกลุ่มอื่น → คูณคะแนนลดครึ่ง
// (เช่น ครูถาม "ครูลืมรหัส" ควรได้วิธีลืมรหัสผ่านของตนเอง ไม่ใช่วิธีที่สถานศึกษารีเซ็ตให้)
const OTHER_ROLE_PENALTY = 0.5;
const HIGH_CONFIDENCE = 2.2;
const LOW_CONFIDENCE = 1.2;

export function normalize(text) {
  let s = String(text || "")
    .normalize("NFC")
    .toLowerCase()
    .replace(/[​-‍﻿]/g, "");
  SYNONYMS.forEach(([re, rep]) => {
    s = s.replace(re, rep);
  });
  return s
    .replace(/[?!.,:;'"“”‘’()[\]{}<>/\\|~`@#$%^&*_+=-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const compact = s => s.replace(/\s+/g, "");

// เตรียม keyword ที่ normalize แล้วไว้ล่วงหน้าครั้งเดียว
const PREPARED = INTENTS.map(intent => ({
  intent,
  keys: [intent.title, ...intent.keywords].map(k => {
    const n = normalize(k);
    return { spaced: n, compact: compact(n), tokens: n.split(" ").filter(Boolean) };
  })
}));

const INTENT_BY_ID = INTENTS.reduce((acc, it) => {
  acc[it.id] = it;
  return acc;
}, {});

const fuse = new Fuse(
  INTENTS.map(it => ({ id: it.id, title: normalize(it.title), keywords: it.keywords.map(normalize) })),
  {
    keys: [
      { name: "title", weight: 0.6 },
      { name: "keywords", weight: 0.4 }
    ],
    includeScore: true,
    ignoreLocation: true,
    threshold: 0.4,
    minMatchCharLength: 2
  }
);

export function getIntent(id) {
  return INTENT_BY_ID[id] || null;
}

/**
 * กลุ่มผู้ใช้จากข้อมูล login ใน localStorage("user")
 * ค่าเดียวกับที่ loginCollege/loginuser/loginuser_gov/loginuserEtc.vue บันทึกไว้
 */
export function detectRole() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return "guest";
    const status = user.user_status;
    if (["A", "B", "C", "G", "S"].includes(status)) return status;
    if (["teacher", "director", "se_director"].includes(status)) return "teacher";
    if (status === "gov_em") return "gov_em";
    if (user.personnel_etc_m_type) return "etc";
  } catch (e) {
    // localStorage ถูกบล็อกหรือข้อมูลเสีย — ถือเป็นผู้ใช้ทั่วไป
  }
  return "guest";
}

export function quickRepliesFor(role) {
  return (QUICK_REPLIES[role] || QUICK_REPLIES.guest).map(getIntent).filter(Boolean);
}

/** คำถามแนะนำในหมวดเดียวกัน โดยเรียงประเด็นที่ตรงกลุ่มผู้ใช้ขึ้นก่อน */
export function intentsInCategory(categoryId, role) {
  return INTENTS.filter(it => it.category === categoryId && !["greeting", "thanks"].includes(it.id)).sort((a, b) => {
    const ra = a.roles && a.roles.includes(role) ? 0 : 1;
    const rb = b.roles && b.roles.includes(role) ? 0 : 1;
    return ra - rb;
  });
}

function keywordHit(key, qSpaced, qCompact, qTokens) {
  // keyword หลายคำ (มีช่องว่าง) ตรงเมื่อพบครบทุกคำ ไม่สนลำดับ/คำแทรก
  // เช่น "thaid ไม่ผ่าน" ตรงกับ "สแกน thaid แล้วไม่ผ่าน"
  if (key.tokens.length > 1) {
    return key.tokens.every(t => qCompact.includes(t));
  }
  // keyword สั้นมาก (เช่น "hi", "ok", "38") ต้องตรงทั้งคำ ไม่งั้นจะไปติดกลางคำอื่น
  if (key.compact.length <= 3) {
    return qTokens.includes(key.spaced) || qCompact === key.compact;
  }
  return qCompact.includes(key.compact) || qSpaced.includes(key.spaced);
}

/**
 * จับคู่คำถาม
 * คืนค่า { intent, confidence: "high" | "medium" | "none", alternatives: Intent[], score }
 */
export function matchQuestion(text, role = "guest") {
  const qSpaced = normalize(text);
  const qCompact = compact(qSpaced);
  const qTokens = qSpaced.split(" ");
  if (!qCompact) return { intent: null, confidence: "none", alternatives: [], score: 0 };

  const scored = PREPARED.map(({ intent, keys }) => {
    let score = 0;
    const counted = new Set(); // "ครูลืมรหัส" กับ "ครู ลืมรหัส" คือคำเดียวกัน นับครั้งเดียว
    keys.forEach(key => {
      if (key.compact && !counted.has(key.compact) && keywordHit(key, qSpaced, qCompact, qTokens)) {
        counted.add(key.compact);
        score += 1 + Math.min(key.compact.length, 20) / 5;
      }
    });
    if (score > 0 && intent.roles) {
      if (intent.roles.includes(role)) score += ROLE_BOOST;
      else if (role !== "guest") score *= OTHER_ROLE_PENALTY;
    }
    return { intent, score };
  })
    .filter(r => r.score > 0)
    .sort((a, b) => b.score - a.score);

  const top = scored[0];
  if (top && top.score >= LOW_CONFIDENCE) {
    const alternatives = scored
      .slice(1, 4)
      .filter(r => r.score >= top.score * 0.6)
      .map(r => r.intent);
    return {
      intent: top.intent,
      confidence: top.score >= HIGH_CONFIDENCE ? "high" : "medium",
      alternatives,
      score: top.score
    };
  }

  // ไม่ตรง keyword ใดเลย → ใช้ fuzzy หาคำถามใกล้เคียงมาเสนอ (ไม่ตอบเอง เพื่อไม่ให้ตอบผิดเรื่อง)
  const fuzzy = fuse
    .search(qSpaced)
    .slice(0, 4)
    .map(r => getIntent(r.item.id))
    .filter(it => it && !["greeting", "thanks"].includes(it.id));
  return { intent: null, confidence: "none", alternatives: fuzzy, score: top ? top.score : 0 };
}

// ---------------------------------------------------------------------------
// ข้อมูลส่วนบุคคลในข้อความ (PDPA) — เตือนผู้ใช้ และปิดบังก่อนส่งไปเก็บ log
// ---------------------------------------------------------------------------
const ID_CARD_RE = /\d[\s-]?\d{4}[\s-]?\d{5}[\s-]?\d{2}[\s-]?\d/g;
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
const PHONE_RE = /0\d{1,2}[\s-]?\d{3}[\s-]?\d{3,4}/g;

export function containsPersonalData(text) {
  const s = String(text || "");
  return new RegExp(ID_CARD_RE.source).test(s) || new RegExp(EMAIL_RE.source).test(s);
}

export function maskPersonalData(text) {
  return String(text || "")
    .replace(ID_CARD_RE, "[เลขบัตร]")
    .replace(EMAIL_RE, "[อีเมล]")
    .replace(PHONE_RE, "[เบอร์โทร]")
    .slice(0, 200);
}

/** ลิงก์ที่กลุ่มผู้ใช้นี้เข้าได้จริง (link ที่ไม่ระบุ roles = ทุกคนเข้าได้) */
export function linksFor(intent, role) {
  return (intent.links || []).filter(l => !l.roles || l.roles.includes(role));
}
